import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  LogOut, Search, Download, RefreshCw, Loader2, Inbox,
} from "lucide-react";
import { API, STATE_OPTIONS, ENQUIRY_STATUSES } from "@/lib/site";
import { AdminArticles } from "@/components/AdminArticles";
import { AdminEpisodes } from "@/components/AdminEpisodes";
import { AdminPartners } from "@/components/AdminPartners";

const TOKEN_KEY = "hs_admin_token";

const STATUS_COLORS = {
  New: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  Contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "In Progress": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  Closed: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#060D17]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#0B192C] to-[#040910] px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-[2rem] border border-white/10 bg-[#0B192C]/70 p-9 backdrop-blur-xl"
      >
        <div className="flex items-center gap-4">
          <img src="/logo.png" alt="Healthcare Solutions" className="h-12 w-auto" />
          <span className="font-mono-x border-l border-white/10 pl-4 text-[10px] uppercase tracking-[0.25em] text-teal-400">
            Admin Portal
          </span>
        </div>
        <div className="mt-8 space-y-5">
          <div>
            <label className="font-mono-x mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-400" htmlFor="admin-email">
              Email
            </label>
            <input id="admin-email" data-testid="admin-login-email" type="email" required
              placeholder="admin email" className={inputCls}
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="font-mono-x mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-400" htmlFor="admin-password">
              Password
            </label>
            <input id="admin-password" data-testid="admin-login-password" type="password" required
              placeholder="password" className={inputCls}
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal-500 px-8 py-4 text-sm font-bold text-[#060D17] transition-all duration-300 hover:bg-teal-400 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <Link to="/" data-testid="admin-back-home" className="mt-5 block text-center text-xs text-slate-500 transition-colors hover:text-teal-300">
          Back to website
        </Link>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("enquiries");

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchEnquiries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (stateFilter !== "All") params.state = stateFilter;
      if (statusFilter !== "All") params.status = statusFilter;
      const { data } = await axios.get(`${API}/enquiries`, { ...authHeaders, params });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Failed to load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, stateFilter, statusFilter, authHeaders]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, authHeaders);
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.full_name, e.organization, e.city, e.email, e.mobile, e.interest]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [enquiries, search]);

  const exportCsv = () => {
    const header = ["Name", "Organization", "Designation", "City", "State", "Mobile", "Email", "Interest", "Message", "Status", "Date"];
    const rows = filtered.map((e) => [
      e.full_name, e.organization, e.designation, e.city, e.state, e.mobile, e.email, e.interest,
      (e.message || "").replace(/\n/g, " "), e.status, new Date(e.created_at).toLocaleString(),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `healthcare-solutions-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  if (!token) {
    return <LoginForm onLogin={(t) => setToken(t)} />;
  }

  const counts = ENQUIRY_STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: enquiries.filter((e) => e.status === s).length }),
    {}
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#060D17]">
      <header className="border-b border-white/5 bg-[#0B192C]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Healthcare Solutions" className="h-10 w-auto" />
            <span className="font-mono-x hidden border-l border-white/10 pl-4 text-[10px] uppercase tracking-[0.25em] text-teal-400 sm:block">
              Admin Portal
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="hidden text-xs text-slate-400 transition-colors hover:text-teal-300 sm:block">
              View website
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 text-xs font-semibold text-slate-300 transition-all hover:border-red-500/40 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div data-testid="admin-tabs" className="mb-8 flex flex-wrap gap-2">
          {[
            ["enquiries", "Enquiries"],
            ["articles", "Articles"],
            ["episodes", "Episodes"],
            ["companies", "Companies"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`rounded-full border px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
                tab === id
                  ? "border-teal-400 bg-teal-500 text-[#060D17]"
                  : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-teal-500/40 hover:text-teal-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (<>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {ENQUIRY_STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`admin-status-${s.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setStatusFilter(statusFilter === s ? "All" : s)}
              className={`rounded-2xl border p-5 text-left transition-all duration-300 ${
                statusFilter === s ? "border-teal-500/50 bg-teal-500/10" : "border-white/5 bg-[#0B192C]/60 hover:border-white/15"
              }`}
            >
              <p className="font-display text-2xl font-bold text-white">{counts[s] || 0}</p>
              <p className="font-mono-x mt-1 text-[10px] uppercase tracking-[0.2em] text-slate-400">{s}</p>
            </button>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-search-input"
              placeholder="Search name, organization, city, email..."
              className={`${inputCls} pl-11`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="admin-filter-state"
            className={`${inputCls} sm:w-56`}
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="All">All States</option>
            {STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex gap-3">
            <button
              onClick={fetchEnquiries}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-5 py-3 text-xs font-semibold text-slate-300 transition-all hover:border-teal-500/40 hover:text-teal-300"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-button"
              className="inline-flex items-center gap-2 rounded-xl bg-teal-500 px-5 py-3 text-xs font-bold text-[#060D17] transition-all hover:bg-teal-400"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-white/5">
          {filtered.length === 0 ? (
            <div data-testid="admin-empty-state" className="flex flex-col items-center gap-3 bg-[#0B192C]/40 py-20 text-center">
              <Inbox className="h-10 w-10 text-slate-600" />
              <p className="text-sm text-slate-400">
                {loading ? "Loading enquiries..." : "No enquiries found yet."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/5 bg-[#0B192C] font-mono-x text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Organization</th>
                    <th className="px-5 py-4">Location</th>
                    <th className="px-5 py-4">Interest</th>
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <Fragment key={e.id}>
                      <tr
                        data-testid={`admin-enquiry-row-${e.id}`}
                        onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                        className="cursor-pointer border-b border-white/5 bg-[#060D17]/40 transition-colors hover:bg-white/[0.03]"
                      >
                        <td className="px-5 py-4">
                          <p className="font-semibold text-white">{e.full_name}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{e.email}</p>
                          <p className="text-xs text-slate-500">{e.mobile}</p>
                        </td>
                        <td className="px-5 py-4 text-slate-300">
                          {e.organization || "—"}
                          {e.designation && <p className="mt-0.5 text-xs text-slate-500">{e.designation}</p>}
                        </td>
                        <td className="px-5 py-4 text-slate-300">
                          {e.city}
                          <p className="mt-0.5 text-xs text-slate-500">{e.state}</p>
                        </td>
                        <td className="px-5 py-4 text-slate-300">{e.interest}</td>
                        <td className="px-5 py-4 text-xs text-slate-400">
                          {new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-5 py-4" onClick={(ev) => ev.stopPropagation()}>
                          <select
                            data-testid={`admin-status-select-${e.id}`}
                            value={e.status}
                            onChange={(ev) => updateStatus(e.id, ev.target.value)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold outline-none ${STATUS_COLORS[e.status]} bg-[#060D17]`}
                          >
                            {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                      {expanded === e.id && (
                        <tr className="border-b border-white/5 bg-[#0B192C]/50">
                          <td colSpan={6} className="px-5 py-5">
                            <p className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-teal-400">Message</p>
                            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300">
                              {e.message || "No message provided."}
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        </>)}

        {tab === "articles" && <AdminArticles />}

        {tab === "episodes" && <AdminEpisodes />}

        {tab === "companies" && <AdminPartners />}
      </main>
    </div>
  );
}
